import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';

interface FormData {
  email: string;
  password: string;
  masterCode: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  masterCode?: string;
  submit?: string;
}

export default function MasterAdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    masterCode: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'Master Admin Login | System Access - Taskbudy';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master administrator login for Taskbudy platform management.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master administrator login for Taskbudy platform management.';
      document.head.appendChild(meta);
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.masterCode.trim()) {
      newErrors.masterCode = 'Master admin code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      // Sign in with Supabase first
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (signInError) {
        throw new Error('Invalid email or password');
      }

      // Verify user is a master admin
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', authData.user.id)
        .eq('role', 'master_admin')
        .single();

      if (adminError || !adminData) {
        await supabase.auth.signOut();
        throw new Error('Access denied. Master admin privileges required.');
      }

      if (!adminData.is_active) {
        await supabase.auth.signOut();
        throw new Error('Your master admin account is inactive. Please contact support.');
      }

      // Verify master admin code (after confirming user exists)
      if (formData.masterCode !== 'TASKBUDY_MASTER_2024') {
        await supabase.auth.signOut();
        throw new Error('Invalid master admin code');
      }

      // Redirect to admin dashboard
      navigate('/admin-dashboard');
    } catch (err: any) {
      console.error('Master admin login error:', err);
      setErrors({ submit: err.message || 'Login failed. Please check your credentials.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        {/* Navigation */}
        <nav className="bg-black/50 backdrop-blur-sm shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div 
                className="flex items-center space-x-2 cursor-pointer transform hover:scale-105 transition-transform duration-300"
                onClick={() => navigate('/')}
              >
                <img 
                  src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/ef22cd57bb320b669eb4db4ff59ba9cf.png" 
                  alt="Taskbudy" 
                  className="h-8 md:h-12"
                />
              </div>

              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors whitespace-nowrap cursor-pointer font-medium"
              >
                Back to Home
              </button>
            </div>
          </div>
        </nav>

        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-orange-600 rounded-2xl flex items-center justify-center shadow-2xl">
                  <i className="ri-shield-keyhole-line text-4xl text-white"></i>
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white mb-2">Master Admin Access</h2>
              <p className="text-gray-400">Restricted access - authorized personnel only</p>
            </div>

            {/* Form */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-gray-700">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-900/50 border ${errors.email ? 'border-red-500' : 'border-gray-600'} rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm text-white placeholder-gray-500`}
                    placeholder="master@taskbudy.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-900/50 border ${errors.password ? 'border-red-500' : 'border-gray-600'} rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm pr-12 text-white placeholder-gray-500`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors cursor-pointer"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-xl`}></i>
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                  )}
                </div>

                {/* Master Admin Code */}
                <div>
                  <label htmlFor="masterCode" className="block text-sm font-medium text-gray-300 mb-2">
                    Master Admin Code
                  </label>
                  <input
                    id="masterCode"
                    name="masterCode"
                    type="password"
                    value={formData.masterCode}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-900/50 border ${errors.masterCode ? 'border-red-500' : 'border-gray-600'} rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm text-white placeholder-gray-500`}
                    placeholder="Enter master verification code"
                  />
                  {errors.masterCode && (
                    <p className="mt-1 text-sm text-red-400">{errors.masterCode}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">This code is only provided to master administrators</p>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-xl">
                    <p className="text-sm text-red-400">{errors.submit}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-red-700 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Verifying Access...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-shield-check-line mr-2"></i>
                      Access Master Dashboard
                    </span>
                  )}
                </button>
              </form>

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-xl">
                <div className="flex items-start">
                  <i className="ri-alert-line text-yellow-500 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-500 mb-1">Security Notice</h4>
                    <p className="text-xs text-yellow-600">All login attempts are monitored and logged. Unauthorized access attempts will be reported.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
